/**
 * Image Quality Checker for OCR
 * 
 * Analyzes images for blur, brightness, and contrast to help users
 * take better photos for text recognition.
 * 
 * Uses native Canvas API - no external dependencies.
 */

import { logger } from '@/lib/logger';

export type WarningSeverity = 'warning' | 'info';

export interface QualityWarning {
    type: 'blur' | 'dark' | 'bright' | 'low_contrast';
    severity: WarningSeverity;
    message: string;
    suggestion: string;
}

export interface QualityMetrics {
    blur: number;        // Laplacian variance (higher = sharper)
    brightness: number;  // Average luminance (0-255)
    contrast: number;    // Std dev of luminance (higher = more contrast)
    width: number;
    height: number;
}

export interface QualityResult {
    metrics: QualityMetrics;
    warnings: QualityWarning[];
    overallScore: number;  // 0-100, higher is better
    isAcceptable: boolean; // true if no severe issues
}

// Thresholds for quality detection
// Slightly sensitive - show helpful hints for suboptimal photos
const THRESHOLDS = {
    blur: {
        veryBlurry: 100,    // < 100 = severe blur warning
        slightlyBlurry: 200 // 100-200 = info, > 200 = acceptable
    },
    brightness: {
        tooDark: 55,        // < 55 = too dark warning
        dark: 80,           // 55-80 = dark info
        bright: 185,        // > 185 = too bright info
        tooBright: 210      // > 210 = too bright warning
    },
    contrast: {
        veryLow: 30,        // < 30 = severe low contrast warning
        low: 45             // 30-45 = low contrast info, > 45 = acceptable
    }
};

// Max thumbnail size for performance
const ANALYSIS_SIZE = 400;

// Skip quality checks on slow devices
const PERFORMANCE_TIMEOUT_MS = 500;
const SKIP_CHECK_KEY = 'dyslexa_skip_quality_check';

/**
 * Main entry point - analyzes image quality and returns warnings
 */
export async function analyzeImageQuality(file: File): Promise<QualityResult | null> {
    // Check if quality checks should be skipped (slow device)
    if (typeof localStorage !== 'undefined' && localStorage.getItem(SKIP_CHECK_KEY) === 'true') {
        logger.debug('Skipping quality check (slow device)');
        return null;
    }

    try {
        const startTime = performance.now();
        
        const result = await performAnalysis(file);
        
        const duration = performance.now() - startTime;
        logger.debug('Quality analysis completed', { 
            duration: Math.round(duration), 
            blur: Math.round(result.metrics.blur),
            brightness: Math.round(result.metrics.brightness),
            contrast: Math.round(result.metrics.contrast),
            score: result.overallScore,
            warnings: result.warnings.length
        });
        
        // If analysis was too slow, skip future checks on this device
        if (duration > PERFORMANCE_TIMEOUT_MS) {
            logger.warn('Quality analysis too slow, disabling for this device', { duration: Math.round(duration) });
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem(SKIP_CHECK_KEY, 'true');
            }
        }
        
        return result;
    } catch (error) {
        logger.warn('Quality analysis failed, skipping', { error: error instanceof Error ? error.message : 'Unknown error' });
        return null;
    }
}

/**
 * Performs the actual image analysis
 */
async function performAnalysis(file: File): Promise<QualityResult> {
    const imageData = await loadImageAsData(file);
    
    const metrics: QualityMetrics = {
        blur: calculateBlurScore(imageData),
        brightness: calculateBrightness(imageData),
        contrast: calculateContrast(imageData),
        width: imageData.width,
        height: imageData.height
    };
    
    const warnings = generateWarnings(metrics);
    const overallScore = calculateOverallScore(metrics);
    const isAcceptable = !warnings.some(w => w.severity === 'warning');
    
    return {
        metrics,
        warnings,
        overallScore,
        isAcceptable
    };
}

/**
 * Loads image file and returns ImageData for analysis
 * Downscales to ANALYSIS_SIZE for performance
 */
async function loadImageAsData(file: File): Promise<ImageData> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
            try {
                // Calculate scale to fit within ANALYSIS_SIZE
                const scale = Math.min(ANALYSIS_SIZE / img.width, ANALYSIS_SIZE / img.height, 1);
                const width = Math.round(img.width * scale);
                const height = Math.round(img.height * scale);
                
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Canvas context unavailable'));
                    return;
                }
                
                ctx.drawImage(img, 0, 0, width, height);
                const imageData = ctx.getImageData(0, 0, width, height);
                
                URL.revokeObjectURL(url);
                resolve(imageData);
            } catch (e) {
                URL.revokeObjectURL(url);
                reject(e);
            }
        };
        
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        
        img.src = url;
    });
}

/**
 * Calculates blur score using Laplacian variance
 * Higher values = sharper image
 */
function calculateBlurScore(imageData: ImageData): number {
    const { data, width, height } = imageData;
    
    // Convert to grayscale array
    const gray = new Float32Array(width * height);
    for (let i = 0; i < gray.length; i++) {
        const idx = i * 4;
        gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
    }
    
    // Apply Laplacian kernel: [0, 1, 0], [1, -4, 1], [0, 1, 0]
    const laplacian: number[] = [];
    
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;
            const lap = 
                gray[idx - width] +                    // top
                gray[idx - 1] +                        // left
                -4 * gray[idx] +                       // center
                gray[idx + 1] +                        // right
                gray[idx + width];                     // bottom
            laplacian.push(lap);
        }
    }
    
    // Calculate variance of Laplacian
    const mean = laplacian.reduce((a, b) => a + b, 0) / laplacian.length;
    const variance = laplacian.reduce((sum, val) => sum + (val - mean) ** 2, 0) / laplacian.length;
    
    return variance;
}

/**
 * Calculates average brightness (luminance)
 * Returns value 0-255
 */
function calculateBrightness(imageData: ImageData): number {
    const { data } = imageData;
    let sum = 0;
    const pixelCount = data.length / 4;
    
    for (let i = 0; i < data.length; i += 4) {
        // Standard luminance formula
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    
    return sum / pixelCount;
}

/**
 * Calculates contrast as standard deviation of luminance
 * Higher values = more contrast
 */
function calculateContrast(imageData: ImageData): number {
    const { data } = imageData;
    const pixelCount = data.length / 4;
    
    // First pass: calculate mean luminance
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
        sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    const mean = sum / pixelCount;
    
    // Second pass: calculate variance
    let varianceSum = 0;
    for (let i = 0; i < data.length; i += 4) {
        const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        varianceSum += (lum - mean) ** 2;
    }
    
    // Standard deviation
    return Math.sqrt(varianceSum / pixelCount);
}

/**
 * Generates user-friendly warnings based on metrics
 */
function generateWarnings(metrics: QualityMetrics): QualityWarning[] {
    const warnings: QualityWarning[] = [];
    
    // Common suggestion for all issues
    const retakeSuggestion = 'Dla lepszych wyników spróbuj ponowić zdjęcie';
    
    // Check blur
    if (metrics.blur < THRESHOLDS.blur.veryBlurry) {
        warnings.push({
            type: 'blur',
            severity: 'warning',
            message: 'Zdjęcie może być niewyraźne',
            suggestion: retakeSuggestion
        });
    } else if (metrics.blur < THRESHOLDS.blur.slightlyBlurry) {
        warnings.push({
            type: 'blur',
            severity: 'info',
            message: 'Zdjęcie może być lekko nieostre',
            suggestion: retakeSuggestion
        });
    }
    
    // Check brightness
    if (metrics.brightness < THRESHOLDS.brightness.tooDark) {
        warnings.push({
            type: 'dark',
            severity: 'warning',
            message: 'Zdjęcie może być zbyt ciemne',
            suggestion: retakeSuggestion
        });
    } else if (metrics.brightness < THRESHOLDS.brightness.dark) {
        warnings.push({
            type: 'dark',
            severity: 'info',
            message: 'Zdjęcie jest dość ciemne',
            suggestion: retakeSuggestion
        });
    } else if (metrics.brightness > THRESHOLDS.brightness.tooBright) {
        warnings.push({
            type: 'bright',
            severity: 'warning',
            message: 'Zdjęcie może być prześwietlone',
            suggestion: retakeSuggestion
        });
    } else if (metrics.brightness > THRESHOLDS.brightness.bright) {
        warnings.push({
            type: 'bright',
            severity: 'info',
            message: 'Zdjęcie jest bardzo jasne',
            suggestion: retakeSuggestion
        });
    }
    
    // Check contrast
    if (metrics.contrast < THRESHOLDS.contrast.veryLow) {
        warnings.push({
            type: 'low_contrast',
            severity: 'warning',
            message: 'Tekst może być trudny do odczytania',
            suggestion: retakeSuggestion
        });
    } else if (metrics.contrast < THRESHOLDS.contrast.low) {
        warnings.push({
            type: 'low_contrast',
            severity: 'info',
            message: 'Kontrast zdjęcia jest niski',
            suggestion: retakeSuggestion
        });
    }
    
    return warnings;
}

/**
 * Calculates overall quality score (0-100)
 */
function calculateOverallScore(metrics: QualityMetrics): number {
    // Normalize each metric to 0-100 scale based on new thresholds
    
    // Blur: 0-500+ mapped to 0-100 (higher blur score = better)
    // 300+ is considered good (slightlyBlurry threshold)
    const blurScore = Math.min(100, (metrics.blur / 500) * 100);
    
    // Brightness: optimal is around 110-140, penalize extremes
    const optimalBrightness = 125;
    const brightnessDeviation = Math.abs(metrics.brightness - optimalBrightness);
    const brightnessScore = Math.max(0, 100 - (brightnessDeviation / 100) * 100);
    
    // Contrast: 0-80+ mapped to 0-100 (higher = better)
    // 55+ is considered good (low threshold)
    const contrastScore = Math.min(100, (metrics.contrast / 80) * 100);
    
    // Weighted average (blur is most important for OCR)
    const score = (blurScore * 0.5) + (brightnessScore * 0.25) + (contrastScore * 0.25);
    
    return Math.round(score);
}

/**
 * Resets the skip flag (for testing or settings)
 */
export function resetQualityCheckSkip(): void {
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(SKIP_CHECK_KEY);
    }
}
